import { RunContextApp } from 'framework/RCApp';
import type {
  BrandGuidelineEntry,
  BrandGuidelineGetResult,
  BrandGuidelineListResult,
  BrandGuidelineMutationResult,
  BrandGuidelinePayload,
} from 'types/apis/brandGuidelinesInterfaces';

const asString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const trimString = (value: unknown): string => asString(value).trim();

const parseNullableDate = (value: unknown): string | null => {
  if (value == null) return null;
  const text = trimString(value);
  return text || null;
};

const parseId = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const mapRecordToEntry = (raw: unknown): BrandGuidelineEntry | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;

  const record = raw as Record<string, unknown>;
  const id = parseId(record.id);
  const configKey = trimString(record.configKey ?? record.config_key);
  const configType = trimString(record.configType ?? record.config_type);
  const category = trimString(record.category);

  if (id == null || !configKey || !configType || !category) return undefined;

  return {
    id,
    configKey,
    configType,
    category,
    createdAt: parseNullableDate(record.createdAt ?? record.created_at),
    updatedAt: parseNullableDate(record.updatedAt ?? record.updated_at),
  };
};

const toApiPayload = (payload: BrandGuidelinePayload) => ({
  config_type: payload.configType.trim(),
  config_key: payload.configKey.trim(),
  category: payload.category.trim(),
});

const unwrapListBody = (body: unknown): { items: unknown[]; totalCount: number } => {
  if (!body || typeof body !== 'object') {
    return { items: [], totalCount: 0 };
  }

  const record = body as Record<string, unknown>;

  if (Array.isArray(record.brand_guidelines)) {
    return {
      items: record.brand_guidelines,
      totalCount: typeof record.totalCount === 'number' ? record.totalCount : record.brand_guidelines.length,
    };
  }

  if (Array.isArray(record.data)) {
    return {
      items: record.data,
      totalCount: typeof record.totalCount === 'number' ? record.totalCount : record.data.length,
    };
  }

  if (Array.isArray(body)) {
    return { items: body, totalCount: body.length };
  }

  return { items: [], totalCount: 0 };
};

const unwrapItemBody = (body: unknown): unknown => {
  if (!body || typeof body !== 'object') return body;

  const record = body as Record<string, unknown>;
  if (record.data && typeof record.data === 'object') return record.data;

  return body;
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return asString((error as { message?: unknown }).message) || fallback;
  }
  return fallback;
};

const sortEntries = (entries: BrandGuidelineEntry[]): BrandGuidelineEntry[] =>
  [...entries].sort((a, b) => {
    const categoryCompare = a.category.localeCompare(b.category);
    if (categoryCompare !== 0) return categoryCompare;
    const typeCompare = a.configType.localeCompare(b.configType);
    if (typeCompare !== 0) return typeCompare;
    return a.configKey.localeCompare(b.configKey);
  });

export const BrandGuidelinesAPI = {
  list: async (rc: RunContextApp): Promise<BrandGuidelineListResult> => {
    try {
      const response = await rc.bffClient.getBrandGuidelines();

      if ('error' in response && response.error) {
        return { success: false, entries: [], totalCount: 0, error: extractErrorMessage(response.error, 'Failed to fetch brand guidelines') };
      }

      const body = 'data' in response ? response.data : response;
      const { items, totalCount } = unwrapListBody(body);
      const entries = sortEntries(
        items.map(mapRecordToEntry).filter((entry): entry is BrandGuidelineEntry => Boolean(entry)),
      );

      return { success: true, entries, totalCount };
    } catch (error) {
      rc.error(
        rc.getName(BrandGuidelinesAPI),
        'Error >> GET request to : /v1/media/brand-guidelines\n\nresulted in error : \n',
        error,
      );

      return {
        success: false,
        entries: [],
        totalCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  getById: async (rc: RunContextApp, id: number | string): Promise<BrandGuidelineGetResult> => {
    try {
      const response = await rc.bffClient.getBrandGuidelineById(String(id));

      if ('error' in response && response.error) {
        return { success: false, error: extractErrorMessage(response.error, 'Failed to fetch brand guideline') };
      }

      const body = 'data' in response ? response.data : response;
      const entry = mapRecordToEntry(unwrapItemBody(body));

      if (!entry) {
        return { success: false, error: 'Invalid brand guideline response from server' };
      }

      return { success: true, entry };
    } catch (error) {
      rc.error(
        rc.getName(BrandGuidelinesAPI),
        `Error >> GET request to : /v1/media/brand-guidelines/${id}\n\nresulted in error : \n`,
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  create: async (rc: RunContextApp, payload: BrandGuidelinePayload): Promise<BrandGuidelineMutationResult> => {
    try {
      const response = await rc.bffClient.createBrandGuideline(toApiPayload(payload));

      if ('error' in response && response.error) {
        return { success: false, error: extractErrorMessage(response.error, 'Failed to create brand guideline') };
      }

      const body = 'data' in response ? response.data : response;
      const entry = mapRecordToEntry(unwrapItemBody(body));

      return { success: true, entry };
    } catch (error) {
      rc.error(
        rc.getName(BrandGuidelinesAPI),
        'Error >> POST request to : /v1/media/brand-guidelines\n\nresulted in error : \n',
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  update: async (
    rc: RunContextApp,
    id: number | string,
    payload: BrandGuidelinePayload,
  ): Promise<BrandGuidelineMutationResult> => {
    try {
      const response = await rc.bffClient.updateBrandGuideline(String(id), toApiPayload(payload));

      if ('error' in response && response.error) {
        return { success: false, error: extractErrorMessage(response.error, 'Failed to update brand guideline') };
      }

      const body = 'data' in response ? response.data : response;
      const entry = mapRecordToEntry(unwrapItemBody(body));

      return { success: true, entry };
    } catch (error) {
      rc.error(
        rc.getName(BrandGuidelinesAPI),
        `Error >> PUT request to : /v1/media/brand-guidelines/${id}\n\nresulted in error : \n`,
        error,
      );

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },
};
